import { useQuery } from "@tanstack/react-query";
import { RoutinesIcon } from "@/shared/icons";
import {
  describeSchedule,
  describeNextRun,
} from "@/modules/routines/schedule-utils";
import type { Routine } from "@/modules/routines/routines-queries";
import { companyRoutinesQueryOptions } from "./queries";
import type { CompanyRoutine } from "./types";

// `describeSchedule` / `describeNextRun` only read `schedule_kind`,
// `schedule_spec`, and `next_run_at` — the other Routine fields aren't
// touched. The cast is safe at runtime and avoids dragging webhook
// metadata that the admin endpoint doesn't return.
function asRoutine(r: CompanyRoutine): Routine {
  return r as unknown as Routine;
}

function lastRunLabel(r: CompanyRoutine): string | null {
  if (!r.last_run_at) return null;
  const d = new Date(r.last_run_at);
  if (Number.isNaN(d.getTime())) return null;
  const date = d.toLocaleDateString("ru-RU", { day: "numeric", month: "short" });
  const time = d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
  return `Последний запуск: ${date} в ${time}`;
}

function RoutineRow({ routine }: { routine: CompanyRoutine }) {
  const schedule = describeSchedule(asRoutine(routine));
  const next = describeNextRun(asRoutine(routine));
  const last = lastRunLabel(routine);
  const failed = routine.last_status === "failed";

  return (
    <div className="company-routine-row">
      <span className="company-routine-icon" aria-hidden>
        <RoutinesIcon />
      </span>
      <div className="company-routine-body">
        <div className="company-routine-title-row">
          <span className="company-routine-name">{routine.name}</span>
          {routine.space && (
            <span
              className="history-item-space"
              title={`Пространство: ${routine.space.name}`}
            >
              {routine.space.name}
            </span>
          )}
          {failed && (
            <span className="company-routine-badge company-routine-badge--failed">
              Последний запуск с ошибкой
            </span>
          )}
        </div>
        <div className="company-routine-meta">
          <span>{schedule}</span>
          {last && <span>{last}</span>}
          {next && <span>{next}</span>}
        </div>
      </div>
    </div>
  );
}

export function CompanyRoutines() {
  const { data = [], isLoading } = useQuery(companyRoutinesQueryOptions());

  return (
    <section>
      <h2 className="company-section-title">
        Активные автоматизации компании
        <span className="company-count-chip">{data.length}</span>
      </h2>
      {isLoading ? (
        <div className="cmd-empty">Загрузка…</div>
      ) : data.length === 0 ? (
        <div className="cmd-empty">
          <p className="cmd-empty-title">Активных автоматизаций нет</p>
          <p className="cmd-empty-hint">
            Включите автоматизацию в любом пространстве — например, утренний дайджест.
          </p>
        </div>
      ) : (
        <div className="company-routines-list">
          {data.map((r) => (
            <RoutineRow key={r.id} routine={r} />
          ))}
        </div>
      )}
    </section>
  );
}
